import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Screen, TopBar, Loader } from "@/components/ui";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { crystalsApi } from "@/data/api";
import { INTENTIONS_LIST } from "@/data/crystalsData";
import { useTheme, fonts } from "@/theme";
import { Field, TextField, Toggle, SaveBar, listToText, textToList } from "@/components/adminui";

const EMPTY = { name: "", aliases: [], intentions: [], keywords: [], what_is: "", look_for: "", spiritual: [], folklore: "", quick_fact: "", content_angles: [], keep_accurate: "", image_url: "", hidden: false };

export default function AdminCrystalEdit() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { token } = useAuth();
  const toast = useToast();
  const [params] = useSearchParams();
  const id = params.get("id");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    crystalsApi.adminGet(id, token).then((d) => setForm({ ...EMPTY, ...d })).catch(() => toast.show("Couldn't load")).finally(() => setLoading(false));
  }, [id, token]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIntention = (it) => setForm((f) => ({ ...f, intentions: f.intentions.includes(it) ? f.intentions.filter((x) => x !== it) : [...f.intentions, it] }));

  const save = async () => {
    if (!form.name.trim()) { toast.show("Name is required"); return; }
    setSaving(true);
    const body = { ...form };
    try {
      if (id) await crystalsApi.adminUpdate(id, body, token);
      else await crystalsApi.adminCreate(body, token);
      toast.show("Saved ✨"); navigate("/admin/crystals");
    } catch (e) { toast.show(e.message || "Couldn't save"); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!id || !window.confirm("Delete this crystal?")) return;
    try { await crystalsApi.adminDelete(id, token); toast.show("Deleted"); navigate("/admin/crystals"); } catch (e) { toast.show(e.message || "Couldn't delete"); }
  };

  if (loading) return <Screen><TopBar title="Edit crystal" /><Loader /></Screen>;

  return (
    <Screen>
      <TopBar title={id ? "Edit crystal" : "New crystal"} />
      <div className="ritzy-fade" style={{ padding: "16px 20px 20px" }}>
        <Field label="Name"><TextField value={form.name} onChange={set("name")} data-testid="crystal-name" placeholder="e.g. Amethyst" /></Field>
        <Field label="Aliases (one per line)"><TextField multiline rows={2} value={listToText(form.aliases)} onChange={(v) => set("aliases")(textToList(v))} /></Field>
        <Field label="Intentions">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {INTENTIONS_LIST.map((it) => {
              const on = form.intentions.includes(it);
              return <button key={it} type="button" onClick={() => toggleIntention(it)} style={{ background: on ? colors.brand : colors.surfaceSecondary, color: on ? colors.onBrand : colors.onSurfaceSecondary, border: `1px solid ${on ? colors.brand : colors.border}`, borderRadius: 999, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: fonts.text }}>{it}</button>;
            })}
          </div>
        </Field>
        <Field label="Keywords (one per line)"><TextField multiline rows={2} value={listToText(form.keywords)} onChange={(v) => set("keywords")(textToList(v))} /></Field>
        <Field label="What is it? (geology)"><TextField multiline value={form.what_is} onChange={set("what_is")} /></Field>
        <Field label="Look for"><TextField multiline rows={2} value={form.look_for} onChange={set("look_for")} /></Field>
        <Field label="Spiritual associations (one per line)"><TextField multiline value={listToText(form.spiritual)} onChange={(v) => set("spiritual")(textToList(v))} /></Field>
        <Field label="Folklore & history"><TextField multiline value={form.folklore} onChange={set("folklore")} /></Field>
        <Field label="Quick fact"><TextField multiline rows={2} value={form.quick_fact} onChange={set("quick_fact")} /></Field>
        <Field label="Content angles (one per line)"><TextField multiline value={listToText(form.content_angles)} onChange={(v) => set("content_angles")(textToList(v))} /></Field>
        <Field label="Keep it accurate (warning)"><TextField multiline rows={2} value={form.keep_accurate} onChange={set("keep_accurate")} /></Field>
        <Field label="Image URL"><TextField value={form.image_url} onChange={set("image_url")} placeholder="https://..." /></Field>
        <Toggle testId="crystal-hidden" label="Hidden from library" checked={form.hidden} onChange={set("hidden")} />
        <SaveBar onSave={save} saving={saving} onDelete={id ? del : undefined} />
      </div>
    </Screen>
  );
}
